import {
    Injectable,
    Signal,
    WritableSignal,
    computed,
    signal,
} from '@angular/core';
import { DatiqGraphqlApolloService } from '@dat/datiq-graphql';
import {
    IDatiqGeoSuggestion,
    IqProxyGeoService,
    iqCurrencyRound,
    iqCurrencyRoundRange,
} from '@dat/iq-proxy';
import { gql } from 'apollo-angular';
import { isEqual } from 'lodash';
import { firstValueFrom } from 'rxjs';
import {
    EquipmentTypes,
    GraphQlSpotRate,
    GraphQlSpotRateSchema,
    GraphQlForecastSchema,
    IIqRateLaneTableLanesStateService,
    ODGeoSearchPair,
    UiLane,
    UiSpotRate,
    emptyUiLane,
    emptyUiSpotRate,
    emptyUiGraphCard,
    GraphQlForecast,
} from '../../models';

@Injectable({
    providedIn: 'root',
})
export class IqRateLaneTableLanesStateService
    implements IIqRateLaneTableLanesStateService
{
    getRows(
        lanes: { origin?: string; destination?: string }[],
        equipmentType: string,
    ): Signal<any[]> {
        throw new Error('Method not implemented.');
    }

    constructor(
        private readonly _apollo: DatiqGraphqlApolloService,
        private readonly _proxyGeoService: IqProxyGeoService,
    ) {}

    getLane(ODPairs: ODGeoSearchPair): Signal<UiLane> {
        const origin: Signal<IDatiqGeoSuggestion> =
            this._proxyGeoService.getSuggestion(ODPairs.origin);
        const destination: Signal<IDatiqGeoSuggestion> =
            this._proxyGeoService.getSuggestion(ODPairs.destination);
        return computed(() => {
            if (
                !origin()?.displayName?.length ||
                !destination()?.displayName?.length
            )
                return emptyUiLane;

            return {
                origin: {
                    ...origin(),
                },
                destination: {
                    ...destination(),
                },
            };
        });
    }

    getSpotRate(
        ODPairs: ODGeoSearchPair,
        equipment: EquipmentTypes,
    ): Signal<UiSpotRate> {
        const responseRate: WritableSignal<UiSpotRate> = signal(
            emptyUiSpotRate,
            {
                equal: isEqual,
            },
        );
        const origin: Signal<IDatiqGeoSuggestion> =
            this._proxyGeoService.getSuggestion(ODPairs.origin);

        const destination: Signal<IDatiqGeoSuggestion> =
            this._proxyGeoService.getSuggestion(ODPairs.destination);

        return computed(() => {
            if (
                this.checkSuggestion(origin)() ||
                this.checkSuggestion(destination)()
            )
                return emptyUiSpotRate;

            firstValueFrom(
                this._apollo.getApollo().query({
                    query: gql`
                        query (
                            $historicalQuery: HISTORICAL_SearchCriteriaInputHistorical
                        ) {
                            rate: getHistorical(query: $historicalQuery) {
                                average: avgAverage {
                                    perTrip {
                                        value: average
                                    }
                                }
                                high: avgHigh {
                                    perTrip {
                                        value: average
                                    }
                                }
                                low: avgLow {
                                    perTrip {
                                        value: average
                                    }
                                }
                            }
                        }
                    `,
                    variables: {
                        historicalQuery: {
                            equipmentCategory: equipment,
                            timeInterval: 'CurrentDay',
                            timeEscalation: 'THIRTYDAYS',
                            destination: {
                                city: destination().city,
                                stateProv: destination().stateProv,
                                postalCode: destination().postalCode,
                            },
                            origin: {
                                city: origin().city,
                                stateProv: origin().stateProv,
                                postalCode: origin().postalCode,
                            },
                            rateType: 'SPOT',
                        },
                    },
                }),
            ).then(({ data }: { data: GraphQlSpotRate }) => {
                const parseResult = GraphQlSpotRateSchema.safeParse(data);

                if (parseResult.success) {
                    responseRate.set({
                        label: iqCurrencyRound(data.rate.average.perTrip.value),
                        subLabel: iqCurrencyRoundRange(
                            data.rate.low.perTrip.value,
                            data.rate.high.perTrip.value,
                        ),
                    });
                }
            });

            return responseRate();
        });
    }

    private checkSuggestion(
        suggestion: Signal<IDatiqGeoSuggestion>,
    ): Signal<boolean> {
        return computed(
            () =>
                !suggestion().postalCode?.length &&
                !suggestion().stateProv?.length &&
                !suggestion().city?.length,
        );
    }

    getForecast52WeeksGraph(
        ODPairs: ODGeoSearchPair,
        equipment: EquipmentTypes,
    ): Signal<any> {
        const graph = signal(
            {
                chart:{
                    data: {
                        actual: [],
                    },
                    current: 0,
                }
            },
            { equal: isEqual },
        );
        const origin: Signal<IDatiqGeoSuggestion> =
            this._proxyGeoService.getSuggestion(ODPairs.origin);

        const destination: Signal<IDatiqGeoSuggestion> =
            this._proxyGeoService.getSuggestion(ODPairs.destination);
        return computed(() => {
            if (
                this.checkSuggestion(origin)() ||
                this.checkSuggestion(destination)()
            )
                return emptyUiGraphCard;

            firstValueFrom(
                this._apollo.getApollo().query({
                    query: gql`
                        query ($forecastQuery: Ratecast_SearchCriteriaInput) {
                            forecast: RatecastPublic(query: $forecastQuery) {
                                rates: forecasts {
                                    trip: perTrip {
                                        average: forecastUSD
                                    }
                                    mile: perMile {
                                        average: forecastUSD
                                    }
                                }
                            }
                        }
                    `,
                    variables: {
                        forecastQuery: {
                            equipmentCategory: equipment,
                            forecastPeriod: 'FIFTYTWOWEEKS',
                            maximumEscalation: 'COUNTRY',
                            destination: {
                                city: destination().city,
                                stateProv: destination().stateProv,
                                postalCode: destination().postalCode,
                            },
                            origin: {
                                city: origin().city,
                                stateProv: origin().stateProv,
                                postalCode: origin().postalCode,
                            },
                            rateType: 'SPOT',
                        },
                    },
                }),
            ).then(({ data }: { data: GraphQlForecast }) => {
                const parseResult = GraphQlForecastSchema.safeParse(data);
                const arr: number[] = data.forecast.rates.trip.map((rates) => {
                    return rates.average
                })

                if (parseResult.success) {
                    graph.set({
                        chart:{
                        data: {
                            actual: arr,
                        },
                        current: 0,
                    }
                    });
                }
            });

            return graph();
        });
    }

    getRow(ODPairs: ODGeoSearchPair, equipment: EquipmentTypes): Signal<any> {
        throw new Error('Method not implemented.');
    }
}
